import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";
import { contact, whatsappUrl } from "@/data/brand";

window.addEventListener("error", (event) => {
  if (event.error instanceof DOMException && event.error.name === "DataCloneError" && event.message?.includes("PerformanceServerTiming")) {
    event.stopImmediatePropagation();
    event.preventDefault();
  }
}, true);

class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidMount() {
    window.setTimeout(() => {
      try {
        window.sessionStorage.removeItem("madminute-dom-recovery");
      } catch (_) {
        // Storage can be unavailable in restricted browser sessions.
      }
    }, 5000);
  }

  componentDidCatch(error, info) {
    console.error("MADMINUTE render error", error, info);
    const message = `${error?.name || ""} ${error?.message || ""}`;
    const isDomMutationError = /removeChild|insertBefore|not a child|NotFoundError/i.test(message);
    if (isDomMutationError) {
      try {
        if (!window.sessionStorage.getItem("madminute-dom-recovery")) {
          window.sessionStorage.setItem("madminute-dom-recovery", "1");
          window.location.reload();
        }
      } catch (_) {
        // The visible recovery screen remains available if storage is blocked.
      }
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <main data-testid="app-error-fallback" style={{ minHeight: "100vh", background: "#ffffff", color: "#09090b", display: "grid", placeItems: "center", padding: 28, textAlign: "center" }}>
          <section>
            <img src="/media/brand/madminute-mark-black.png" alt="MADMINUTE official logo" style={{ width: 58, height: 72, objectFit: "contain", margin: "0 auto 12px" }} />
            <p style={{ color: "#ff3b30", letterSpacing: ".18em", textTransform: "uppercase", fontWeight: 700 }}>MADMINUTE</p>
            <h1 style={{ fontSize: "clamp(42px, 7vw, 82px)", margin: "12px 0" }}>LET'S GET YOU BACK ON TRACK.</h1>
            <p style={{ color: "#52525b", maxWidth: 560, lineHeight: 1.7 }}>The interactive view could not initialize in this browser session. Reload the experience or contact our team directly.</p>
            <div style={{ display: "flex", justifyContent: "center", gap: 12, flexWrap: "wrap", marginTop: 28 }}>
              <button data-testid="app-error-reload-button" onClick={() => window.location.reload()} style={{ border: 0, background: "#ff3b30", color: "white", padding: "15px 22px", cursor: "pointer", fontWeight: 700 }}>RELOAD WEBSITE</button>
              <a data-testid="app-error-whatsapp-link" href={whatsappUrl("Hi MADMINUTE, I need help with the website.")} style={{ border: "1px solid #09090b", color: "#09090b", padding: "15px 22px", textDecoration: "none", fontWeight: 700 }}>WHATSAPP US</a>
            </div>
            <div style={{ display: "flex", justifyContent: "center", gap: 18, flexWrap: "wrap", marginTop: 22, fontSize: 13 }}>
              <a data-testid="app-error-phone-link" href={`tel:${contact.phoneHref}`} style={{ color: "#52525b" }}>{contact.phone}</a>
              <a data-testid="app-error-email-link" href={`mailto:${contact.email}`} style={{ color: "#52525b" }}>{contact.email}</a>
              <a data-testid="app-error-instagram-link" href={`https://instagram.com/${contact.instagram}`} target="_blank" rel="noreferrer" style={{ color: "#52525b" }}>@{contact.instagram}</a>
            </div>
          </section>
        </main>
      );
    }
    return this.props.children;
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <AppErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </AppErrorBoundary>,
);
